import { useSyncExternalStore } from "react";

export interface AppBuild {
  version: string;
  commit: string;
  builtAt: string;
}

/** The build this page is running. */
export const CURRENT_BUILD: AppBuild = __APP_BUILD__;

/**
 * How a build is named on screen: the commit when the build was given one, otherwise its
 * UTC build time as a compact stamp. Either is enough to tell two deploys apart.
 */
export function buildLabel(build: AppBuild): string {
  if (build.commit) {
    return build.commit;
  }

  const stamp = build.builtAt.replace(/[-:]/g, "");

  return `${stamp.slice(0, 8)}.${stamp.slice(9, 13)}`;
}

/** A different deploy, not merely a later one: a rollback is an update too. */
export function isDifferentBuild(current: AppBuild, latest: AppBuild): boolean {
  return latest.builtAt !== current.builtAt || latest.commit !== current.commit;
}

export type UpdateStatus = "idle" | "checking" | "current" | "available" | "failed";

interface UpdateState {
  status: UpdateStatus;
  latest: AppBuild | null;
  dismissed: boolean;
}

let state: UpdateState = { status: "idle", latest: null, dismissed: false };
const listeners = new Set<() => void>();

function setState(next: Partial<UpdateState>): void {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

/**
 * Asks the server which build it is serving now.
 *
 * `no-store`, and a query string the service worker and every cache in between have
 * never seen: the answer is only worth anything if it did not come from a cache.
 */
export async function checkForUpdate(): Promise<UpdateStatus> {
  setState({ status: "checking" });

  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });

    if (!response.ok) {
      throw new Error(`version.json answered ${response.status}`);
    }

    const latest = (await response.json()) as AppBuild;
    const status = isDifferentBuild(CURRENT_BUILD, latest) ? "available" : "current";

    setState({ status, latest });

    return status;
  } catch {
    setState({ status: "failed" });

    return "failed";
  }
}

/**
 * Moves the page onto the newest deploy.
 *
 * The worker is asked to update first, so a changed `sw.js` is in charge before the
 * reload rather than after the next one. Then a plain reload: navigations are network
 * first in the worker and `index.html` is served `no-cache`, so the reload fetches the
 * new shell, and the new shell names new hashed assets.
 */
export async function applyUpdate(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    await registration?.update();
  } catch {
    // A worker that cannot update is no reason not to reload; the shell still will.
  }

  window.location.reload();
}

export function dismissUpdate(): void {
  setState({ dismissed: true });
}

/** Fifteen minutes. Long enough to cost nothing, short enough to catch a deploy today. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

let watching = false;

/**
 * Checks on launch, every time the app comes back to the foreground, and on an interval.
 *
 * The foreground check is the one that matters on a phone. An installed app is rarely
 * launched — iOS resumes it from memory, with the page it had yesterday — so without
 * this the only way onto a new deploy was to remove the app and add it again.
 *
 * Production only: in development there is no version.json and Vite reloads by itself.
 */
export function watchForUpdates(): void {
  if (watching || !import.meta.env.PROD || window.location.protocol === "capacitor:") {
    return;
  }

  watching = true;

  void checkForUpdate();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void checkForUpdate();
    }
  });

  window.setInterval(() => {
    if (document.visibilityState === "visible") {
      void checkForUpdate();
    }
  }, CHECK_INTERVAL_MS);
}

export function useAppUpdate(): UpdateState {
  return useSyncExternalStore(subscribe, () => state);
}
